import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Star, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

function Stars({ value, onChange, size = "h-5 w-5" }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange?.(n)}
          data-testid={`review-star-${n}`}
          className="hover:scale-110 transition-transform"
        >
          <Star
            className={`${size} ${n <= value ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`}
          />
        </button>
      ))}
    </div>
  );
}

export default function VendorReviews({ vendorId, onChange }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/vendors/${vendorId}/reviews`);
    setReviews(data);
  };
  useEffect(() => { if (vendorId) load(); }, [vendorId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) return toast.error("Please log in to review");
    setBusy(true);
    try {
      const { data } = await api.post(`/vendors/${vendorId}/reviews`, { rating, title, body });
      toast.success("Review submitted");
      setTitle(""); setBody(""); setRating(5);
      await load();
      // Backend now returns the freshly-recomputed vendor rating inline
      onChange?.({ rating: data.vendor_rating, rating_count: data.vendor_rating_count });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review");
    } finally {
      setBusy(false);
    }
  };

  const deleteMine = async () => {
    if (!user) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/vendors/${vendorId}/reviews/mine`);
      toast.success("Review deleted");
      await load();
      onChange?.({ rating: data.vendor_rating, rating_count: data.vendor_rating_count });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete review");
    } finally {
      setBusy(false);
    }
  };

  const my = reviews.find((r) => r.user_id === user?.user_id);

  // Prefill the form with the user's existing review (if any) so they see it.
  useEffect(() => {
    if (my) {
      setRating(my.rating);
      setTitle(my.title || "");
      setBody(my.body || "");
    }
  }, [my?.review_id]);

  return (
    <div className="space-y-6">
      <Card className="p-5 border-border/60">
        <h3 className="font-heading text-lg">{my ? "Update your review" : "Write a review"}</h3>
        <form onSubmit={submit} className="space-y-3 mt-3">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">Your rating:</span>
            <Stars value={rating} onChange={setRating} />
          </div>
          <Input
            placeholder="Title (optional)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="review-title-input"
          />
          <Textarea
            rows={3}
            placeholder="Share your honest experience"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            data-testid="review-body-input"
          />
          <div className="flex items-center gap-2">
            <Button type="submit" disabled={busy} className="rounded-full" data-testid="review-submit-btn">
              {busy ? "Saving…" : my ? "Update review" : "Submit review"}
            </Button>
            {my && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={deleteMine}
                className="rounded-full"
                data-testid="review-delete-btn"
              >
                Delete my review
              </Button>
            )}
          </div>
        </form>
      </Card>

      <div className="space-y-3">
        {reviews.map((r) => (
          <Card key={r.review_id} className="p-4 border-border/60 flex gap-3" data-testid={`review-item-${r.review_id}`}>
            <Avatar className="h-10 w-10">
              <AvatarImage src={r.user_picture} />
              <AvatarFallback>{r.user_name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-semibold">{r.user_name}</div>
                  {r.verified_buyer && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300 text-[10px] uppercase tracking-[0.14em] px-2 py-0.5 font-semibold"
                      data-testid={`review-verified-${r.review_id}`}
                      title="This reviewer has chatted with the vendor"
                    >
                      <BadgeCheck className="h-3 w-3" /> Verified buyer
                    </span>
                  )}
                </div>
                <Stars value={r.rating} size="h-4 w-4" />
              </div>
              {r.title && <div className="text-sm font-medium mt-1">{r.title}</div>}
              {r.body && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{r.body}</p>}
              <div className="text-[11px] text-muted-foreground mt-2">
                {new Date(r.created_at).toLocaleDateString()}
              </div>
            </div>
          </Card>
        ))}
        {reviews.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-6">No reviews yet — be the first!</p>
        )}
      </div>
    </div>
  );
}

